// src/AppShell.jsx
import React, { useEffect, useRef, useState } from "react";
import styled from "@emotion/styled";
import { OverscrollProvider } from "../context/overscrollProvider";
import { usePullToRefresh } from "../hooks/usePullToRefresh";

const Shell = styled.div`
    label: scroll-container-welcome;
    display: flex;
    flex-direction: column;
    align-items: ${(props) => props.alignItems || 'center'};
    justify-content: ${(props) => props.justifyContent || 'flex-start'};
    height: calc(var(--vh, 1vh) * 100);
    position: relative;
    overflow-y: auto;
    scroll-behavior: smooth;
    -webkit-overflow-scrolling: touch;
    overscroll-behavior-y: none; /* гасим родительские резины */
    touch-action: pan-y;          /* разрешаем вертикальные жесты */
`;

// обновляет --vh под высоту вьюпорта (нужно для iOS/мобильной клавиатуры)
function useViewportHeightVar() {
  useEffect(() => {
    const setVh = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty("--vh", `${vh}px`);
    };
    setVh();
    window.addEventListener("resize", setVh);
    window.addEventListener("orientationchange", setVh);
    return () => {
      window.removeEventListener("resize", setVh);
      window.removeEventListener("orientationchange", setVh);
    };
  }, []);
}

export default function AppShell({ children }) {
  const shellRef = useRef(null);
  const [pathname, setPathname] = useState(window.location.pathname);
  useViewportHeightVar();

  // Listen for pathname changes
  useEffect(() => {
    const checkPathname = () => {
      setPathname(window.location.pathname);
    };
    
    // Check on interval since we can't use useLocation here
    const interval = setInterval(checkPathname, 200);
    
    // Also listen to popstate (browser navigation)
    window.addEventListener('popstate', checkPathname);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('popstate', checkPathname);
    };
  }, []);

  // Enable pull-to-refresh only on main pages: home (/), profile, events, explore
  // Disable everywhere else (login, modals, edit screens, notifications, admin, etc.)
  const mainPages = ['/', '/profile', '/events', '/explore'];
  const isMainPage = mainPages.some(page => 
    pathname === page || 
    (page !== '/' && pathname.startsWith(page + '/'))
  );
  
  // Check if it's NOT a disabled page
  const isDisabledPage = pathname.includes('/login') || 
                         pathname.includes('/start') || 
                         pathname.includes('/onboarding') ||
                         pathname.includes('/edit-') ||
                         pathname.includes('/add-event') ||
                         pathname.includes('/notifications') ||
                         pathname.includes('/reset-password') ||
                         pathname.includes('/admin');
  
  const shouldEnablePullToRefresh = isMainPage && !isDisabledPage;

  // Pull-to-refresh for mobile/PWA - attached to the Shell scroll container
  usePullToRefresh({
    enabled: shouldEnablePullToRefresh,
    targetElement: shellRef, // Pass ref object, hook will handle .current
    onRefresh: () => {
      return new Promise((resolve) => {
        // Check if online before attempting reload
        if (!navigator.onLine) {
          console.warn('Device is offline - skipping reload');
          setTimeout(() => resolve(), 500);
          return;
        }

        // Set timeout: if reload doesn't happen in 2 seconds, reset UI
        const timeoutId = setTimeout(() => {
          console.warn('Page reload timeout (2s) - resetting pull-to-refresh UI');
          resolve();
        }, 2000);

        try {
          window.location.reload(true);
        } catch (error) {
          clearTimeout(timeoutId);
          console.error('Page reload failed:', error);
          resolve();
        }
      });
    }
  });

  return (
    <OverscrollProvider rootRef={shellRef}>
      <Shell id="app-scroll-root" ref={shellRef}>{children}</Shell>
    </OverscrollProvider>
  );
}
